// @flow
import { fromJS, Map } from 'immutable';

import { createSelector } from 'reselect';
import { uniq, flowRight, pluck, some } from 'lodash/fp';

import { getHub, getParentHasCapability } from '~/core/state/devices/selectors';
import { deviceModels, deviceTypes, hublessDeviceTypes, manufacturers } from '~/core/constants/devices';
import { getIdFromProps, getCapabilityNameFromProps, stateIdentity } from '~/core/state/selectors';
import type { StateType } from './types';

export const getAllProducts = (state: { products: StateType }) => state.products;
export const getProductsErrorStatus = (state: { products: StateType }) => state.products.hasError;
export const getProductsIsFetchingStatus = (state: { products: StateType }) => state.products.isFetching;
export const getProductsLoadedStatus = (state: { products: StateType }) => state.products.isLoaded;

export const getProductId = (product: typeof fromJS) => product.get('id');

export const getProductItems = createSelector(getAllProducts, products => products.productItems || Map());

export const getProductItemsExcludingGroups = createSelector(getProductItems, productItems => {
    return productItems.filterNot(p => p.get('isGroup'));
});

export const getGroups = createSelector(getProductItems, productItems => {
    return productItems.filter(p => p.get('isGroup'));
});

export const doesProductExist = createSelector([getProductItems, getIdFromProps], (productItems, id) =>
    productItems.some(product => product.get('id') === id)
);

export const getProductsLoadedAt = createSelector(getAllProducts, products => products.loadedAt);

export const allProductsByTypeSelector = (types: any, products: typeof fromJS) => {
    const normalisedTypes = Array.isArray(types) ? types : [types];
    const items = products.filter(x => normalisedTypes.includes(x.get('type')) && x.getIn(['props', 'online']));

    return items;
};

export const allProductsIncludingOfflineByTypeSelector = (types: any, products: typeof fromJS) => {
    const normalisedTypes = Array.isArray(types) ? types : [types];
    return products.filter(x => normalisedTypes.includes(x.get('type')));
};

export const allProductsByTypeSelectorFactory = (typeNames: any) =>
    createSelector([getProductItems], (productItems: typeof fromJS) =>
        allProductsByTypeSelector(typeNames, productItems)
    );

export const productByIdSelector = (id: string, products: typeof fromJS) => products.find(x => x.get('id') === id);

// Depricated in favor of getProductItemById
// Will replace all usages in another PR
export const productByIdSelectorFactory = (id: string) =>
    createSelector([getProductItems], (productItems: typeof fromJS) => productByIdSelector(id, productItems));

export const getProductItemById = createSelector(getProductItems, getIdFromProps, (productItems, id) =>
    productItems.find(prod => prod.get('id') === id)
);

export const getIsGroupForProduct = createSelector(getProductItemById, product => product && product.get('isGroup'));

export const makeGetProductItemById = () =>
    createSelector(getProductItems, getIdFromProps, (productItems, id) =>
        productItems.find(prod => prod.get('id') === id, null, Map())
    );

export const getModeForProduct = createSelector(
    getProductItemById,
    productItem => productItem && productItem.getIn(['state', 'mode'])
);

export const getNameForProduct = createSelector(
    getProductItemById,
    productItem => productItem && productItem.getIn(['state', 'name'])
);

export const getStateForProduct = createSelector(
    getProductItemById,
    productItem => productItem && productItem.get('state')
);

export const getTypeForProduct = createSelector(
    getProductItemById,
    (product = new Map()) => (product.get('isGroup') ? deviceTypes.PRODUCT_GROUP : product.get('type'))
);

export const getPropsForProduct = createSelector(
    getProductItemById,
    product => product && product.get('props', fromJS({}))
);

export const getPreviousConfigForProduct = createSelector(
    getProductItemById,
    product => product && product.getIn(['props', 'previous'])
);

export const getProductCapabilities = createSelector(
    getProductItemById,
    product => product && product.getIn(['props', 'capabilities'], fromJS([]))
);

export const productItemPropSelectorFactory = (id: string, prop: string) =>
    createSelector([productByIdSelectorFactory(id)], product => product && product.getIn(['props', prop]));

export const firstHolidaymodeProduct = (state: Object) =>
    state.products && state.products.productItems && state.products.productItems.size > 0
        ? state.products.productItems.find(prod => prod.getIn(['props', 'holidayMode', 'enabled']) === true) ||
          fromJS({})
        : fromJS({});

export const firstProductTypeSelectorFactory = (type: string) =>
    createSelector([allProductsByTypeSelectorFactory(type)], products => products.first());

const getZoneFromProps = (_, props) => (props && props.zone) || '';
const getTypeFromProps = (_, props) => (props && props.type) || '';
const getParentFromProps = (_, props) => (props && props.parent) || '';

export const getProductsByZone = createSelector(
    [getProductItems, getZoneFromProps],
    (products, zone) => products.filter(product => product.getIn(['props', 'zone']) === zone) || fromJS({})
);

export const getHubProducts = createSelector(getProductItems, products =>
    products.filter(p => !hublessDeviceTypes.includes(p.get('type')))
);

export const getHublessProducts = createSelector(getProductItems, products =>
    products.filter(p => hublessDeviceTypes.includes(p.get('type')))
);

export const hasParentHub = createSelector(
    [getProductItemById, getHub],
    (product, hub) => product && hub && product.get('parent') === hub.id
);

export const hasSLT1Thermostat = createSelector(getProductItems, products =>
    products.some(p => p.getIn(['props', 'model']) === deviceModels.SLT1)
);

export const getProductsByTypeList = createSelector(
    [getProductItems, (_, props) => (props && props.types) || []],
    (products, types) => products.filter(p => types.includes(p.get('type')))
);

export const hasMultipleZones = createSelector(
    getProductItems,
    products => products.filter(product => product.get('type') === 'heating').size > 1
);

const getSameZoneProductByType = createSelector([getProductsByZone, getTypeFromProps], (products, type) =>
    // $FlowFixMe
    products.filter(z => z.get('type') === type).getIn([0], fromJS({}))
);

export const getSameZoneScheduleByType = createSelector(getSameZoneProductByType, product =>
    product.getIn(['state', 'schedule'])
);

export const getProductsByParent = createSelector(
    [getProductItems, getParentFromProps],
    (products, parent) => products.filter(p => p.get('parent') === parent) || fromJS([])
);

export const getIdFromParentByType = createSelector([getProductsByParent, getTypeFromProps], (products, type) =>
    // $FlowFixMe
    products
        .filter(p => p.get('type') === type)
        .get(0, fromJS({}))
        .get('id', undefined)
);

export const getPowerConsumptionForProduct = createSelector(
    getProductItemById,
    product => product && product.getIn(['props', 'powerConsumption'])
);

export const getOnSinceForProduct = createSelector(
    getProductItemById,
    product => product && product.getIn(['props', 'onSince'])
);

export const getProductError = createSelector(getProductItemById, product => product.getIn(['error']));

const getCapabilityFromProps = (_, props) => (props && props.capability) || '';

export const getWhetherProductHasCapability = createSelector(
    [getProductItemById, getCapabilityFromProps],
    (product, capability) => product.getIn(['props', 'capabilities'], []).includes(capability)
);

export const doesAnyProductHaveCapability = createSelector(
    [getProductItems, getCapabilityFromProps],
    (products, capability) => !!products.filter(p => p.getIn(['props', 'capabilities'], []).includes(capability)).size
);

export const getProductsWithParentCapability = createSelector(
    [getProductItems, stateIdentity, getCapabilityNameFromProps],
    (products, state, capabilityName) =>
        products.filter(p => getParentHasCapability(state, { id: p.get('id'), capabilityName }))
);

// productItem functions

export const productItemStatePropSelectorFactory = (id: string, propName: string) =>
    createSelector([productByIdSelectorFactory(id)], product => product.getIn(['state', propName]));

export const getHasWiringError = createSelector(getProductItemById, product => !!product.get('error'));

export const productModeSelectorFactory = (id: string) => productItemStatePropSelectorFactory(id, 'mode');

export function getProductIndexById(products: StateType, id: string) {
    return products.productItems.findIndex(x => x.get('id') === id);
}

export function getProductIndexByZoneId(products: StateType, id: string) {
    return products.productItems.findIndex(x => x.getIn(['props', 'zone']) === id);
}

export const getHasHeating = createSelector(getProductItems, products =>
    products.some(p => [deviceTypes.HEATING, deviceTypes.NA_THERMOSTAT].includes(p.get('type')))
);

export const getIsProductHeatingOrHotWater = createSelector(
    [getProductItems, getProductItemById],
    (products, product) =>
        !!product && products.some(p => [deviceTypes.HEATING, deviceTypes.HOT_WATER].includes(product.get('type')))
);

export const getFirstHeatingProductId = createSelector(getProductItems, products => {
    const prod = products.find(p => [deviceTypes.HEATING, deviceTypes.NA_THERMOSTAT].includes(p.get('type')));
    return prod && prod.get('id');
});

export const getStatusForProduct = createSelector(
    getProductItemById,
    productItem => productItem && productItem.getIn(['state', 'status'])
);

export const getProductTypesUserOwns = createSelector([getProductItems], products =>
    flowRight(uniq, pluck('type'))(products.toJS())
);

export const getIsHueProduct = createSelector(
    getProductItemById,
    (product = Map()) => product.getIn(['props', 'manufacturer']) === manufacturers.PHILIPS
);

export const getIsInGroups = createSelector(
    getProductItemById,
    productItem => productItem && some(productItem.get('inGroups'))
);

export const getFirstScheduledEvent = createSelector(
    getProductItemById,
    product => product && product.getIn(['state', 'schedule', 'monday', 0])
);



// WEBPACK FOOTER //
// ./src/core/state/products/selectors.js